import { useUserStore } from '../stores/userStore'
import { useAuthStore } from '../stores/oauthStore'
import { fetchOAuthToken, defaultGrantType } from './oauthService'
import { fetchCrmToken } from './userService'

export const getToken = async (usePublicToken = false, bookingStarted = false): Promise<string> => {
  const userStore = useUserStore()
  const authStore = useAuthStore()

  // For logged in users, always prioritize CRM token unless explicitly asked for public token
  if (
    !usePublicToken &&
    userStore.isLoggedIn &&
    userStore.token &&
    userStore.tokenExpirationTime &&
    Date.now() < userStore.tokenExpirationTime
  ) {
    return userStore.token
  }

  // If CRM token is expired but user is logged in, refresh it
  if (
    !usePublicToken &&
    userStore.isLoggedIn &&
    userStore.token &&
    userStore.tokenExpirationTime &&
    Date.now() >= userStore.tokenExpirationTime
  ) {
    try {
      if (!userStore.user || !userStore.user.email || !userStore.password) {
        throw new Error('Missing user credentials')
      }
      const tokenResponse = await fetchCrmToken(userStore.user.email, userStore.password)
      const newToken = tokenResponse.access_token
      userStore.setToken(newToken)
      return newToken
    } catch (error) {
      userStore.logout()
      // Fall through to get public token
    }
  }

  // For non-logged-in users during booking process, use existing booking token if valid
  if (
    bookingStarted &&
    authStore.token &&
    authStore.tokenExpirationTime &&
    Date.now() < authStore.tokenExpirationTime
  ) {
    return authStore.token
  }

  // For non-logged-in users in booking process, create new booking token
  if (bookingStarted && !userStore.isLoggedIn) {
    const token = await fetchOAuthToken(defaultGrantType)
    const expirationTime = Date.now() + 20 * 60 * 1000 // 20 minutes
    authStore.setToken(token, expirationTime, 'booking')
    return token
  }

  // Get or refresh OAuth token for public access
  if (
    usePublicToken ||
    !userStore.isLoggedIn ||
    !userStore.token ||
    !userStore.tokenExpirationTime ||
    Date.now() >= userStore.tokenExpirationTime
  ) {
    const token = await fetchOAuthToken(defaultGrantType)
    console.log('[TokenService] Created new public token')
    return token
  }

  throw new Error('Could not retrieve access token')
}

// These functions are now deprecated as we use the auth store
export const setBookingToken = () => {
  console.warn('setBookingToken is deprecated, use authStore.setToken instead')
}

export const clearBookingToken = () => {
  console.warn('clearBookingToken is deprecated, use authStore.clearToken instead')
  const authStore = useAuthStore()
  authStore.clearToken()
}
